/**
 * The names of the tasks this context raises for held items; what each one is lives in
 * `holdTaskCatalog`. Mirrors the projector's `HOLD_TASK_TYPES`, which `backendContract` pins: a type
 * renamed on one side would leave its tasks with no card.
 */
export const HOLD_TASK_TYPES = {
  CLIENT_COVERAGE_MISSING: 'revenueCycle.clientCoverageMissing',
  CLIENT_COVERAGE_UNVERIFIED: 'revenueCycle.clientCoverageUnverified',
  CLIENT_COVERAGE_ON_HOLD: 'revenueCycle.clientCoverageOnHold',
  CLIENT_POLICYHOLDER: 'revenueCycle.clientPolicyholder',
  CLIENT_COVERAGE_PAYER: 'revenueCycle.clientCoveragePayer',
  CLIENT_AUTHORIZATIONS: 'revenueCycle.clientAuthorizations',
  CLIENT_DIAGNOSIS: 'revenueCycle.clientDiagnosis',
  CLIENT_DEMOGRAPHICS: 'revenueCycle.clientDemographics',
  PAYER_NOT_IN_EFFECT: 'revenueCycle.payerNotInEffect',
  PAYER_INCOMPLETE_SETUP: 'revenueCycle.payerIncompleteSetup',
  PAYER_UNSUPPORTED_BILLING: 'revenueCycle.payerUnsupportedBilling',
  PAYER_NO_MODIFIER_RULE: 'revenueCycle.payerNoModifierRule',
  PAYER_TOO_MANY_MODIFIERS: 'revenueCycle.payerTooManyModifiers',
  CHARGE_MASTER: 'revenueCycle.chargeMaster',
  CHARGE_FOR_CODE: 'revenueCycle.chargeForCode',
  SERVICE_NOT_BILLABLE: 'revenueCycle.serviceNotBillable',
  SERVICE_CODE_INVALID: 'revenueCycle.serviceCodeInvalid',
  SESSION_SERVICE_MISSING: 'revenueCycle.sessionServiceMissing',
  SESSION_SERVICE_DUPLICATE: 'revenueCycle.sessionServiceDuplicate',
  SESSION_PLACE_OF_SERVICE: 'revenueCycle.sessionPlaceOfService',
  SESSION_PLAN_SERVICE_MISSING: 'revenueCycle.sessionPlanServiceMissing',
  SESSION_PLAN_PLACE_OF_SERVICE: 'revenueCycle.sessionPlanPlaceOfService',
  SESSION_PERFORMER: 'revenueCycle.sessionPerformer',
  SESSION_NOT_PAYABLE: 'revenueCycle.sessionNotPayable',
  STAFF_NPI: 'revenueCycle.staffNpi',
  STAFF_QUALIFICATION: 'revenueCycle.staffQualification',
};

/** What a hold task is about. */
export const HOLD_SUBJECT = {
  CLIENT: 'client',
  PAYER: 'payer',
  TREATMENT_SERVICE: 'treatmentService',
  CODE: 'code',
  CLINIC: 'clinic',
  SESSION: 'sessionActivity',
  STAFF: 'staff',
  SESSION_PLAN: 'sessionPlan',
};

/** Where a billing item names each kind of subject. Mirrors the projector's `SUBJECT_FIELD`. */
export const HOLD_SUBJECT_FIELD = {
  [HOLD_SUBJECT.CLIENT]: 'clientId',
  [HOLD_SUBJECT.PAYER]: 'fundingSourceId',
  [HOLD_SUBJECT.TREATMENT_SERVICE]: 'treatmentServiceId',
  [HOLD_SUBJECT.CODE]: 'billingCode',
  [HOLD_SUBJECT.CLINIC]: 'clinicId',
  [HOLD_SUBJECT.SESSION]: 'sessionActivityId',
  [HOLD_SUBJECT.STAFF]: 'performingUserId',
  [HOLD_SUBJECT.SESSION_PLAN]: 'sessionPlanDeclaration',
};

/**
 * A session plan subject names the plan and the activity type its delivery is declared for, as
 * `<plan id>:<type>`: one plan declares one delivery per type.
 *
 * @param {string} subjectId
 * @returns {{sessionPlanId: string, activityType: string}|null}
 */
export const parseSessionPlanDeclaration = (subjectId) => {
  const [sessionPlanId, activityType] = typeof subjectId === 'string' ? subjectId.split(':') : [];

  return sessionPlanId && activityType ? { sessionPlanId, activityType } : null;
};

/**
 * Who owns hold tasks, as Identity & Tenancy names teams: a client's tasks one by one, any other
 * task by its subject, and billing otherwise. Mirrors the projector's, which `backendContract` pins.
 */
export const CLIENT_TASK_TEAM = {
  [HOLD_TASK_TYPES.CLIENT_COVERAGE_MISSING]: 'intake',
  [HOLD_TASK_TYPES.CLIENT_COVERAGE_UNVERIFIED]: 'intake',
  [HOLD_TASK_TYPES.CLIENT_COVERAGE_ON_HOLD]: 'intake',
  [HOLD_TASK_TYPES.CLIENT_POLICYHOLDER]: 'intake',
  [HOLD_TASK_TYPES.CLIENT_COVERAGE_PAYER]: 'intake',
  [HOLD_TASK_TYPES.CLIENT_AUTHORIZATIONS]: 'billing',
  [HOLD_TASK_TYPES.CLIENT_DIAGNOSIS]: 'billing',
  [HOLD_TASK_TYPES.CLIENT_DEMOGRAPHICS]: 'intake',
};
export const SUBJECT_TEAM = { staff: 'credentialing' };
export const DEFAULT_TASK_TEAM = 'billing';

/** The teams whose work includes hold tasks, all of which need the billing privilege to see. */
export const HOLD_TASK_TEAMS = [
  ...new Set([...Object.values(CLIENT_TASK_TEAM), ...Object.values(SUBJECT_TEAM), DEFAULT_TASK_TEAM]),
];
